import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LogOut, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { useTicketCounts } from '@/hooks/useTicketCounts'
import type { TicketCounts } from '@/api/ticketsApi'
import type { AuthSession } from '@/lib/authToken'
import type { TFunction } from 'i18next'
import title21Mark from '@/assets/title21-mark.png'

/** Queue entries; `to` carries the queue as a query param on /queue. */
const QUEUES: { key: string; labelKey: string; to: string; count: keyof TicketCounts; warn?: boolean }[] = [
  { key: 'all', labelKey: 'nav.allOpen', to: '/queue', count: 'allOpen' },
  { key: 'mine', labelKey: 'nav.assignedToMe', to: '/queue?queue=mine', count: 'assignedToMe' },
  { key: 'sla_risk', labelKey: 'nav.slaAtRisk', to: '/queue?queue=sla_risk', count: 'slaAtRisk', warn: true },
  { key: 'gxp', labelKey: 'nav.gxpImpacting', to: '/queue?queue=gxp', count: 'gxpImpacting' },
  { key: 'escalated', labelKey: 'nav.escalated', to: '/queue?queue=escalated', count: 'escalated' },
]

/** `key` is the API product key (`GET /products`), used as `?product=`. */
const PRODUCTS: { key: string; labelKey: string }[] = [
  { key: 'cgt', labelKey: 'products.cgt' },
  { key: 'eqms', labelKey: 'products.eqms' },
  { key: 'smart_qc', labelKey: 'products.smartQc' },
  { key: 'doc_control', labelKey: 'products.documentControl' },
  { key: 'training', labelKey: 'products.training' },
  { key: 'integrations', labelKey: 'products.integrations' },
  { key: 'platform', labelKey: 'products.platform' },
]

// Exact values from artboard 01 "Sidebar".
const itemBase = 'flex items-center justify-between rounded-md px-2.5 text-body transition-colors'
const itemIdle = 'text-sidebar-text hover:bg-sidebar-active/60'
const itemActive = 'bg-sidebar-active font-medium text-white'

function SectionHeading({ children }: { children: string }) {
  return (
    <div className="px-2 pb-2">
      <p className="text-eyebrow font-medium uppercase text-sidebar-heading">{children}</p>
    </div>
  )
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''
  return (first + last).toUpperCase()
}

/**
 * Footer subtitle. Artboard 01 reads "Tier 2 · Validated env.", which is the
 * agent's `title` from /auth/me; without a title, build it from tier and team.
 */
function userSubtitle(user: AuthSession, t: TFunction): string {
  if (user.title) return user.title
  if (user.kind === 'CUSTOMER') return t('user.customer')
  if (user.tier) {
    const tier = t('user.tier', { tier: user.tier })
    return user.teamName && user.teamName !== tier ? `${tier} · ${user.teamName}` : tier
  }
  if (user.teamName) return user.teamName
  return user.isSuperAdmin ? t('user.superAdmin') : t('user.agent')
}

export function Sidebar() {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const location = useLocation()
  const { data: counts } = useTicketCounts()
  const canAnalytics = usePermission(PERMISSIONS.analyticsRead)
  const canSla = usePermission(PERMISSIONS.slaManage)
  const canRouting = usePermission(PERMISSIONS.routingManage)
  const canAdminSla = canSla || canRouting
  // The catalog (products, clients, tenants) is superadmin-only — role permissions don't grant it.
  const isSuperAdmin = !!user?.isSuperAdmin
  const canCreate = usePermission(PERMISSIONS.ticketsCreate)
  const isCustomer = user?.kind === 'CUSTOMER'
  // Customers see their organisation's tickets only: no agent work queues.
  const queues = isCustomer ? QUEUES.filter((q) => q.key === 'all') : QUEUES

  const params = new URLSearchParams(location.search)
  const onQueue = location.pathname === '/queue'
  const activeQueue = onQueue && !params.get('product') ? (params.get('queue') ?? 'all') : null
  const activeProduct = onQueue ? params.get('product') : null

  const subtitle = user ? userSubtitle(user, t) : ''

  return (
    <aside className="flex h-full w-[232px] shrink-0 flex-col gap-7 overflow-y-auto bg-sidebar px-4 py-6 text-label">
      <div className="flex items-center gap-2.5 px-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-white p-[3px]">
          <img src={title21Mark} alt="" className="h-auto w-full" />
        </div>
        <div className="flex flex-col">
          <span className="text-brand font-semibold text-white">{t('brand.name')}</span>
          <span className="text-label text-sidebar-subtle">{t('brand.product')}</span>
        </div>
      </div>

      {canCreate ? (
        <nav className="flex flex-col gap-0.5" aria-label={t('nav.tickets')}>
          <NavLink
            to="/tickets/new"
            className={({ isActive }) => cn(itemBase, 'justify-start gap-2 py-2', isActive ? itemActive : itemIdle)}
          >
            <Plus className="size-4" aria-hidden />
            <span>{isCustomer ? t('nav.newTicket') : t('nav.logTicket')}</span>
          </NavLink>
        </nav>
      ) : null}

      <nav className="flex flex-col gap-0.5" aria-label={t('nav.queues')}>
        <SectionHeading>{t('nav.queues')}</SectionHeading>
        {queues.map((q) => {
          const active = activeQueue === q.key
          const count = counts?.[q.count]
          return (
            <NavLink key={q.key} to={q.to} className={cn(itemBase, 'py-2', active ? itemActive : itemIdle)}>
              <span>{t(isCustomer ? 'nav.myOrgTickets' : q.labelKey)}</span>
              {typeof count === 'number' ? (
                <span
                  className={cn(
                    'font-mono text-label',
                    active ? 'text-white' : q.warn ? 'text-sidebar-warn' : 'text-sidebar-subtle',
                  )}
                >
                  {count}
                </span>
              ) : null}
            </NavLink>
          )
        })}
      </nav>

      <nav className="flex flex-col gap-0.5" aria-label={t('nav.byProduct')}>
        <SectionHeading>{t('nav.byProduct')}</SectionHeading>
        {PRODUCTS.map((p) => (
          <NavLink
            key={p.key}
            to={`/queue?product=${p.key}`}
            className={cn(itemBase, 'py-[7px]', activeProduct === p.key ? itemActive : itemIdle)}
          >
            {t(p.labelKey)}
          </NavLink>
        ))}
      </nav>

      {canAnalytics || canAdminSla || isSuperAdmin ? (
        <nav className="flex flex-col gap-0.5" aria-label={t('nav.admin')}>
          <SectionHeading>{t('nav.admin')}</SectionHeading>
          {canAnalytics ? (
            <NavLink to="/analytics" className={({ isActive }) => cn(itemBase, 'py-[7px]', isActive ? itemActive : itemIdle)}>
              {t('nav.analytics')}
            </NavLink>
          ) : null}
          {canAdminSla ? (
            <NavLink to="/admin/sla" className={({ isActive }) => cn(itemBase, 'py-[7px]', isActive ? itemActive : itemIdle)}>
              {t('nav.slaPolicies')}
            </NavLink>
          ) : null}
          {isSuperAdmin ? (
            <>
              <NavLink to="/admin/products" className={({ isActive }) => cn(itemBase, 'py-[7px]', isActive ? itemActive : itemIdle)}>
                {t('nav.catalogProducts')}
              </NavLink>
              <NavLink to="/admin/clients" className={({ isActive }) => cn(itemBase, 'py-[7px]', isActive ? itemActive : itemIdle)}>
                {t('nav.catalogClients')}
              </NavLink>
            </>
          ) : null}
        </nav>
      ) : null}

      <div className="grow" />

      {user ? (
        <div className="flex items-center gap-2.5 border-t border-sidebar-border px-2 pt-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-2xl bg-sidebar-avatar">
            <span className="text-label font-semibold text-white">{initials(user.name)}</span>
          </div>
          <div className="flex min-w-0 grow flex-col">
            <span className="truncate text-[13px] font-medium leading-4 text-white">{user.name}</span>
            <span className="truncate text-label text-sidebar-subtle">{subtitle}</span>
          </div>
          <button
            type="button"
            onClick={logout}
            title={t('nav.signOut')}
            aria-label={t('nav.signOut')}
            className="shrink-0 rounded-md p-1 text-sidebar-subtle hover:bg-sidebar-active hover:text-white"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      ) : null}
    </aside>
  )
}
